import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../../assets/scripts/services/apiClient';
import authService from '../../assets/scripts/auth/authService';
import JsonEditor from './JsonEditor';

const TemplateManagement = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [jsonValid, setJsonValid] = useState(true);
  const [jsonValue, setJsonValue] = useState({});

  useEffect(() => {
    const run = async () => {
      const ok = await authService.requireAuth();
      if (!ok) return;
      loadTemplates();
    };
    run();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.get('/integration-templates');
      setTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setName('');
    setJsonValue({ name: '', fields: [] });
    setJsonValid(true);
    setModalOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setName(tpl?.template_data?.name || '');
    setJsonValue(tpl?.template_data || {});
    setJsonValid(true);
    setModalOpen(true);
  };

  const saveTemplate = async () => {
    if (!name.trim() || !jsonValid) {
      alert('Please provide a name and valid JSON.');
      return;
    }
    const payload = { template_data: { ...jsonValue, name: name.trim() } };
    try {
      if (editing) {
        await apiClient.put(`/integration-templates/${editing.id}`, payload);
        alert('Template updated');
      } else {
        await apiClient.post('/integration-templates', payload);
        alert('Template created');
      }
      setModalOpen(false);
      await loadTemplates();
    } catch (e) {
      alert('Failed to save template');
    }
  };

  const deleteTemplate = async (id) => {
    if (!confirm('Delete this template?')) return;
    try {
      await apiClient.delete(`/integration-templates/${id}`);
      await loadTemplates();
      alert('Template deleted');
    } catch (e) {
      alert('Failed to delete template');
    }
  };

  const filtered = useMemo(() => {
    if (!filter) return templates;
    const f = filter.toLowerCase();
    return templates.filter((t) => (t?.template_data?.name || '').toLowerCase().includes(f));
  }, [filter, templates]);

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <>
      <div className="peers ai-c jc-sb fxw-nw mB-20">
        <div className="peer">
          <div className="input-group" style={{ maxWidth: 360 }}>
            <input
              type="text"
              className="form-control"
              placeholder="Filter by name"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <button className="btn btn-outline-secondary" onClick={() => setFilter('')}>Clear</button>
          </div>
        </div>
        <div className="peer">
          <button className="btn btn-primary" onClick={openCreate}>
            <i className="ti-plus mR-5"></i>Create Template
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped table-bordered">
          <thead>
            <tr>
              <th>Name</th>
              <th>Fields</th>
              <th>Created</th>
              <th>Updated</th>
              <th style={{ width: 220 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center">No templates found.</td>
              </tr>
            ) : (
              filtered.map((tpl) => {
                const fields = Array.isArray(tpl?.template_data?.fields) ? tpl.template_data.fields : [];
                return (
                  <tr key={tpl.id}>
                    <td>{tpl?.template_data?.name || 'Untitled'}</td>
                    <td>
                      {fields.length > 0 ? fields.join(', ') : 'N/A'}
                    </td>
                    <td>{tpl.create_date ? new Date(tpl.create_date).toLocaleString() : ''}</td>
                    <td>{tpl.update_date ? new Date(tpl.update_date).toLocaleString() : ''}</td>
                    <td>
                      <button className="btn btn-sm btn-secondary me-2" onClick={() => openEdit(tpl)}>
                        <i className="ti-pencil"></i> Edit
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => deleteTemplate(tpl.id)}>
                        <i className="ti-trash"></i> Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <>
          <div
            className="modal-backdrop show"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1040
            }}
            onClick={() => setModalOpen(false)}
          />
          <div className="modal show d-block" style={{ zIndex: 1050 }} tabIndex="-1">
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editing ? 'Edit Template' : 'Create Template'}</h5>
                  <button type="button" className="btn-close" onClick={() => setModalOpen(false)} aria-label="Close" />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Template Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Template JSON</label>
                    <JsonEditor
                      value={jsonValue}
                      onChange={(valid, val) => {
                        setJsonValid(valid);
                        if (valid && val) setJsonValue(val);
                      }}
                      height={400}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={saveTemplate} disabled={!name.trim() || !jsonValid}>
                    Save Template
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default TemplateManagement;
